import { useMemo } from 'react'
import { Tag } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { hasAnyRole, ROLES } from '@/auth/roles'
import { useI18n } from '@/i18n'
import { Badge, Banner, Card, CardBody, EmptyState, Skeleton, useToast } from '@/ui'
import { commitServerState, type RetagPayload } from '../commands'
import { GateScreen } from '../components/GateScreen'
import { GuardedScanButton } from '../components/Scan'
import { useSupervisorPin } from '../components/SupervisorPin'
import { retag } from '../data/actions'
import { useGate } from '../data/useGate'

interface Waiting {
  key: string
  target: 'BAG' | 'SAMPLE'
  container_id: string | null
  sample_id: string | null
  conflict: string | null
  lot_code: string | null
}

/**
 * F4.6-h — supervisor's re-tag screen: bags and samples the server kept without a tag,
 * from the sync results of this phone's deliveries. Each gets a new tag or seal (retag command).
 */
export function RetagPage() {
  const { t } = useI18n()
  const toast = useToast()
  const actor = useActor()
  const g = useGate()
  const sup = useSupervisorPin()

  const waiting = useMemo<Waiting[]>(() => {
    const done = new Set(
      g.outbox
        .filter((r) => r.type === 'retag')
        .map((r) => {
          const p = r.payload as RetagPayload
          return p.container_id ?? p.sample_id ?? ''
        }),
    )
    const out: Waiting[] = []
    for (const r of g.outbox) {
      if (r.type !== 'commit_delivery') continue
      const st = commitServerState.safeParse(r.result?.server_state ?? {})
      if (!st.success) continue
      for (const w of st.data.waiting_for_tag) {
        const id = w.container_id ?? w.sample_id ?? ''
        if (!id || done.has(id)) continue
        out.push({
          key: id,
          target: w.target,
          container_id: w.container_id ?? null,
          sample_id: w.sample_id ?? null,
          conflict: w.conflict_qr_payload ?? null,
          lot_code: w.lot_code ?? null,
        })
      }
    }
    return out
  }, [g.outbox])

  if (!g.ready || !g.roster) return <Skeleton className="h-64 w-full" />
  const isSup = hasAnyRole(actor.roles, [ROLES.SUPERVISOR])
  const roster = g.roster
  const allRolls = new Set(roster.rolls.map((r) => r.batch_id))

  return (
    <GateScreen title="gate.retag.title">
      {!isSup && <Banner tone="warn">{t('gate.retag.supOnly')}</Banner>}
      {waiting.length === 0 ? (
        <EmptyState
          icon={<Tag />}
          title={t('gate.retag.none')}
          description={t('gate.retag.noneHint')}
          compact
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {waiting.map((w) => (
            <li key={w.key}>
              <Card>
                <CardBody className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[14px]">{w.lot_code ?? '—'}</span>
                    <Badge tone="warn">
                      {t(w.target === 'BAG' ? 'gate.retag.bag' : 'gate.retag.sample')}
                    </Badge>
                  </div>
                  {w.conflict && (
                    <div className="text-[13px] text-muted">
                      {t('gate.retag.conflict', { code: w.conflict })}
                    </div>
                  )}
                  <GuardedScanButton
                    size="touch"
                    label={t(w.target === 'BAG' ? 'gate.retag.scanTag' : 'gate.retag.scanSeal')}
                    guard={{
                      kind: w.target === 'BAG' ? 'BAG' : 'SEAL',
                      operatorId: actor.user_id,
                      rolls: roster.rolls,
                      confirmedBatchIds: allRolls,
                      boundPayloads: g.used.bound,
                      voidedPayloads: g.used.voided,
                      inDraft: new Set(),
                    }}
                    onAccept={async (payload, entry) => {
                      const s = await sup.request(t('gate.retag.supReason'))
                      if (!s) return
                      await retag(g.app, s.user_id, {
                        target: w.target,
                        container_id: w.container_id,
                        sample_id: w.sample_id,
                        qr_payload: payload,
                        entry,
                      })
                      toast.success(t('gate.retag.done', { code: payload }))
                    }}
                  />
                </CardBody>
              </Card>
            </li>
          ))}
        </ul>
      )}
      {sup.sheet}
    </GateScreen>
  )
}
